import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Button, Card, FormGroup, PageHeader } from "@cofano/cck";
import { CircularProgress, Grid2 as Grid } from "@mui/material";

import Helmet from "components/helmet/Helmet";
import Angler from "components/angler/Angler";

import { DataFormat, Language } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { GetVoyagePlanCompactDocument, GetVoyagePlanQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";

export type VoyagePlanFromQuery = GetVoyagePlanQuery["voyagePlan"]

const VoyagePlan = () => {

	const { id } = useParams();
	const navigate = useNavigate();

	const { data, loading }  = useQuery(GetVoyagePlanCompactDocument, {
		variables: {
			id: Number(id),
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("ANGLER_VOYAGE_PLAN")}
					<Helmet title={intl.formatMessage({ id: "ANGLER_VOYAGE_PLAN" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(`/view/voyages/${id}/details`)} variant="outlined">
						{Language("VOYAGE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid container spacing={3}>
				<Grid size={5}>
					<Card>
						{loading ? (
							<CircularProgress size={24} />
						) : (
							<React.Fragment>
								<FormGroup label={Language("DURATION")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.minutesToTime(data?.voyagePlan?.durationMin ?? 0)}
								</FormGroup>

								<FormGroup label={Language("DISTANCE")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.distance(data?.voyagePlan?.distanceKm ?? 0)}
								</FormGroup>

								<FormGroup label={Language("ANGLER_WAITING_TIME")} labelPlacement="left-center" overflow="hidden">
									{DataFormat.minutesToTime(data?.voyagePlan?.totalWaitingTimeMin ?? 0)}
								</FormGroup>

								<FormGroup label={Language("START_TIME")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={data?.voyagePlan?.startTime} withoutYear />
								</FormGroup>

								<FormGroup label={Language("END_TIME")} labelPlacement="left-center" overflow="hidden">
									<FormattedDate date={data?.voyagePlan?.endTime} withoutYear />
								</FormGroup>

								{data?.voyagePlan?.lowestMeasuringPointDepth && (
									<React.Fragment>
										<FormGroup label={Language("MEASURING_POINT")} labelPlacement="left-center" overflow="hidden">
											{data?.voyagePlan.lowestMeasuringPointDepth.name}
										</FormGroup>

										<FormGroup label={Language("ANGLER_WATER_LEVEL")} labelPlacement="left-center" overflow="hidden">
											{DataFormat.simpleUnit(data?.voyagePlan.lowestMeasuringPointDepth.measurement?.waterLevel, DataFormat.UNITS.CENTIMETER)}
										</FormGroup>

										<FormGroup label={Language("ANGLER_KEEL_CLEARANCE")} labelPlacement="left-center" overflow="hidden">
											{DataFormat.simpleUnit(data?.voyagePlan.lowestMeasuringPointDepth.keelClearanceCm, DataFormat.UNITS.CENTIMETER)}
										</FormGroup>

										<FormGroup label={Language("ANGLER_WATERWAY_DEPTH")} labelPlacement="left-center" overflow="hidden">
											{DataFormat.simpleUnit(data?.voyagePlan.lowestMeasuringPointDepth.measurement?.waterwayDepthCm, DataFormat.UNITS.CENTIMETER)}
										</FormGroup>

										<FormGroup label={Language("TIMESTAMP")} labelPlacement="left-center" overflow="hidden">
											<FormattedDate date={data?.voyagePlan.lowestMeasuringPointDepth.measurement?.timestamp} withoutYear />
										</FormGroup>

									</React.Fragment>
								)}
							</React.Fragment>
						)}
					</Card>
				</Grid>

				<Grid size={7}>
					<Angler voyageId={Number(id)} />
				</Grid>
			</Grid>
		</React.Fragment>
	);
};

export default VoyagePlan;
